/**
 * conversionTracker — lightweight event logger for measuring funnel performance
 * by traffic source (TikTok, Facebook, Instagram, Direct, …).
 *
 * FIRST-TOUCH model:
 *  - An anonymous `visitor_id` is persisted in localStorage (survives tabs,
 *    navigations and sessions) so one person is counted as one visitor.
 *  - The FIRST valid external acquisition source is persisted and never
 *    overwritten by self-referrals (lupuslogix.com → lupuslogix.com),
 *    internal navigation, or later Direct visits.
 *  - Preview/dev traffic is flagged `is_internal` so it can be excluded from
 *    public marketing conversion reporting.
 *
 * Fire-and-forget; never throws to the caller. Analytics only.
 */
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import {
  canonicalizeSource,
  isInternalHostname,
  DIRECT_SOURCE,
  INTERNAL_SOURCE,
} from "@/lib/sourceAttribution";

const FIRST_TOUCH_KEY = "lupuslogix_first_touch_v2";
const VISITOR_ID_KEY = "lupuslogix_visitor_id_v1";
const SESSION_ID_KEY = "lupuslogix_session_id_v1";
/** Legacy per-session attribution (kept only for migration of active tabs). */
const LEGACY_SESSION_KEY = "lupuslogix_attribution_v1";

export type ConversionEventName =
  | "landing_view"
  | "page_view"
  | "cta_waitlist_click"
  | "cta_waitlist_success"
  | "cta_signin_click"
  | "auth_signup_attempt"
  /** New-user account-creation screen was displayed (pre-submit funnel step). */
  | "signup_page_view"
  /** Visitor meaningfully began filling the new-account form (pre-submit step). */
  | "signup_form_started"
  | "auth_signup_success"
  | "auth_signup_pending_confirmation"
  | "auth_signin_success"
  | "auth_email_confirmed"
  | "hero_cta_click"
  | "pricing_card_click"
  | "mobile_sticky_trial_cta_click"
  | "pricing_view"
  | "checkout_started"
  | "trial_started"
  | "subscription_purchased"
  /** Press-kit page opened via a tagged media-outreach link (UTM present). */
  | "press_kit_visit"
  /** Source details (referrer, user agent, classification) for every press-kit open. */
  | "press_kit_source"
  /** A homepage section became meaningfully visible (once per session). */
  | "section_view"
  /** 25/50/75/90% scroll-depth milestone (once per session per page). */
  | "scroll_depth";

type Attribution = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  referrer: string | null;
  landing_path: string | null;
  first_seen_at: string | null;
  is_internal: boolean;
};

const EMPTY_ATTRIBUTION: Attribution = {
  source: null,
  medium: null,
  campaign: null,
  content: null,
  term: null,
  referrer: null,
  landing_path: null,
  first_seen_at: null,
  is_internal: false,
};

const referrerHost = (referrer: string): string | null => {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.replace(/^www\./, "") || null;
  } catch {
    return null;
  }
};

/** Attribution candidate derived from the CURRENT page load. */
const currentTouch = (): Attribution => {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || "";
  const refHost = referrerHost(referrer);
  // `src` supports short campaign links (e.g. /auth?src=setup_reminder).
  // Priority: utm_source > src > referrer host > fbclid (Facebook) > Direct.
  // fbclid is only a last-resort fallback so explicit Instagram/UTM/referrer
  // attribution always wins. Prospective only — stored rows are untouched.
  const rawSource =
    params.get("utm_source") || params.get("src") || refHost || (params.get("fbclid") ? "fbclid" : null);
  const internalVisit = isInternalHostname(window.location.hostname);

  const canonical = rawSource
    ? canonicalizeSource(rawSource)
    : { source: DIRECT_SOURCE, kind: "direct" as const };

  // A LupusLogix → LupusLogix referral is internal navigation, not acquisition.
  // A return from Stripe Checkout / Google account screens ("service") is not
  // acquisition either — it must never overwrite a real first touch.
  const source =
    canonical.kind === "self" || canonical.kind === "service" || canonical.kind === "unknown"
      ? DIRECT_SOURCE
      : canonical.source;

  return {
    source: internalVisit ? INTERNAL_SOURCE : source,
    medium: params.get("utm_medium"),
    campaign: params.get("utm_campaign"),
    content: params.get("utm_content"),
    term: params.get("utm_term"),
    referrer: referrer || null,
    landing_path: window.location.pathname + window.location.search,
    first_seen_at: new Date().toISOString(),
    is_internal: internalVisit || canonical.kind === "internal",
  };
};

const isExternal = (source: string | null): boolean =>
  !!source && source !== DIRECT_SOURCE && source !== INTERNAL_SOURCE;

/**
 * Returns the preserved first-touch attribution, upgrading it only when the
 * stored source is weaker (Direct/Internal) than a newly-seen external source.
 */
const readAttribution = (): Attribution => {
  if (typeof window === "undefined") return EMPTY_ATTRIBUTION;

  let stored: Attribution | null = null;
  try {
    const cached = localStorage.getItem(FIRST_TOUCH_KEY);
    if (cached) stored = JSON.parse(cached) as Attribution;
  } catch {
    /* ignore */
  }

  const candidate = currentTouch();

  let next: Attribution;
  if (!stored) {
    next = candidate;
  } else if (!isExternal(stored.source) && isExternal(candidate.source)) {
    // First real external source wins over a previous Direct/Internal touch.
    next = { ...candidate, first_seen_at: stored.first_seen_at || candidate.first_seen_at };
  } else {
    // Preserve first touch. Keep flagging the *current* surface as internal so
    // preview sessions never pollute public marketing numbers.
    next = { ...stored, is_internal: stored.is_internal || candidate.is_internal };
  }

  try {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(next));
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    /* ignore */
  }
  return next;
};

const getVisitorId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = localStorage.getItem(VISITOR_ID_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
    return id;
  } catch {
    return `v_${Date.now()}`;
  }
};

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
};

export const trackConversion = (
  event: ConversionEventName,
  metadata: Record<string, unknown> = {},
): void => {
  const attribution = readAttribution();
  const session_id = getSessionId();
  const visitor_id = getVisitorId();
  (async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user_id = userData?.user?.id ?? null;
      await supabase.from("conversion_events").insert({
        event_name: event,
        user_id,
        session_id,
        visitor_id,
        source: attribution.source,
        medium: attribution.medium,
        campaign: attribution.campaign,
        content: attribution.content,
        term: attribution.term,
        first_seen_at: attribution.first_seen_at,
        is_internal: attribution.is_internal,
        referrer: attribution.referrer,
        landing_path: attribution.landing_path,
        metadata: metadata as never,
      });
    } catch (err) {
      logger.warn("[conversionTracker] insert failed", { event, err });
    }
  })();
};

/**
 * Navigation-safe conversion writer for links that immediately leave a page.
 * The request starts synchronously with `keepalive`, so browser and in-app
 * navigation cannot cancel it as easily as the regular async client insert.
 */
export const trackNavigationConversion = (
  event: ConversionEventName,
  metadata: Record<string, unknown> = {},
  dedupeKey: string = event,
): Promise<void> => {
  if (typeof window !== "undefined") {
    const key = `lupuslogix_nav_conversion_${dedupeKey}`;
    try {
      // Short-window dedupe: guards against accidental double-clicks/double-fires
      // while still counting genuine repeat clicks later in the session.
      const last = Number(sessionStorage.getItem(key) || 0);
      if (last && Date.now() - last < 30_000) return Promise.resolve();
      sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage can be unavailable in private browsing; delivery still proceeds.
    }
  }

  const attribution = readAttribution();
  const session_id = getSessionId();
  const visitor_id = getVisitorId();
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  const backendUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

  if (!publishableKey || !backendUrl) return Promise.resolve();

  return fetch(`${backendUrl}/rest/v1/conversion_events`, {
    method: "POST",
    keepalive: true,
    headers: {
      apikey: publishableKey,
      Authorization: `Bearer ${publishableKey}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify({
      event_name: event,
      user_id: null,
      session_id,
      visitor_id,
      source: attribution.source,
      medium: attribution.medium,
      campaign: attribution.campaign,
      content: attribution.content,
      term: attribution.term,
      first_seen_at: attribution.first_seen_at,
      is_internal: attribution.is_internal,
      referrer: attribution.referrer,
      landing_path: attribution.landing_path,
      metadata,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        logger.warn("[conversionTracker] navigation insert failed", {
          event,
          status: response.status,
        });
      }
    })
    .catch((err) => {
      logger.warn("[conversionTracker] navigation insert failed", { event, err });
    });
};

/**
 * Record an event at most once per visitor within `windowMs`. Used so that a
 * refresh or back-navigation does not inflate signup attempts.
 */
export const trackConversionOnce = (
  event: ConversionEventName,
  windowMs = 30 * 60 * 1000,
  metadata: Record<string, unknown> = {},
): void => {
  if (typeof window === "undefined") return;
  const key = `lupuslogix_ce_once_${event}`;
  try {
    const last = Number(localStorage.getItem(key) || 0);
    if (last && Date.now() - last < windowMs) return;
    localStorage.setItem(key, String(Date.now()));
  } catch {
    /* ignore — fall through and track */
  }
  trackConversion(event, metadata);
};

export const primeAttribution = (): void => {
  readAttribution();
  getSessionId();
  getVisitorId();
};

/** Read-only accessor (debug/verification). */
export const getFirstTouchAttribution = (): Attribution => readAttribution();

/**
 * Identity + attribution context for events that must be written by the
 * server (e.g. Stripe Checkout Session creation, where the browser is
 * navigating away and a client-side insert would be cancelled mid-flight).
 */
export const getAnalyticsContext = () => {
  const attribution = readAttribution();
  return {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    source: attribution.source,
    medium: attribution.medium,
    campaign: attribution.campaign,
    content: attribution.content,
    term: attribution.term,
    first_seen_at: attribution.first_seen_at,
    is_internal: attribution.is_internal,
    referrer: attribution.referrer,
    landing_path: attribution.landing_path,
  };
};